import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { selectExtendedVariant, extendedMaySource } from "../../../extendedVariantSelection";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

import {
  combo,
  fractionLatex,
  isValueCorrect,
  scoreParts,
  setField,
  toLatexExpression,
} from "./conditionalProbabilityUtils";

const TYPE = "MaturaConditionalDiceFacesOpenTask";

const FACE_TEXT = {
  1: "jedno oczko",
  2: "dwa oczka",
  3: "trzy oczka",
  4: "cztery oczka",
  5: "pięć oczek",
  6: "sześć oczek",
};

const COUNT_TEXT = {
  1: "jeden raz",
  2: "dwa razy",
  3: "trzy razy",
};

const ORIGINAL = { n: 4, conditionFace: 5, conditionCount: 2, targetFace: 6 };
const FACES = [1, 2, 3, 4, 5, 6];
const CONFIGS = [4, 5, 6].flatMap((n) => [1, 2, 3].flatMap((conditionCount) =>
  FACES.flatMap((conditionFace) => FACES.filter((targetFace) => targetFace !== conditionFace)
    .map((targetFace) => ({ n, conditionFace, conditionCount, targetFace })))
)).filter((c) => !(c.n === 4 && c.conditionFace === 5 && c.conditionCount === 2 && c.targetFace === 6));
export function getVariantCount() { return CONFIGS.length; }

function rand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeTask(config = rand(CONFIGS)) {
  const otherSlots = config.n - config.conditionCount;
  const conditionTotal = combo(config.n, config.conditionCount) * Math.pow(5, otherSlots);
  const intersectionTotal =
    combo(config.n, config.conditionCount) *
    (Math.pow(5, otherSlots) - Math.pow(4, otherSlots));
  const probability = intersectionTotal / conditionTotal;

  return {
    type: TYPE,
    points: 3,
    ...config,
    otherSlots,
    conditionTotal,
    intersectionTotal,
    probability,
  };
}

export function generateTask(options = {}) {
  const original = options.variant === "original";
  const config = original ? ORIGINAL : selectExtendedVariant(CONFIGS, options);
  return { ...makeTask(config), source: extendedMaySource("4", original) };
}

export function renderTask(task) {
  return (
    <div className="space-y-4">
      <p>
        <strong>Zadanie maturalne - otwarte (0-3 pkt).</strong>
      </p>
      <p>
        Doświadczenie losowe polega na <InlineMath math={String(task.n)}/>-krotnym rzucie symetryczną
        sześcienną kostką do gry, która na każdej ścianie ma inną liczbę oczek
        - od jednego oczka do sześciu oczek.
      </p>
      <p>
        <strong>
          Oblicz prawdopodobieństwo zdarzenia polegającego na tym, że otrzymamy
          co najmniej jeden raz {FACE_TEXT[task.targetFace]}, pod warunkiem że
          otrzymamy dokładnie {COUNT_TEXT[task.conditionCount]}{" "}
          {FACE_TEXT[task.conditionFace]}. Zapisz obliczenia.
        </strong>
      </p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  return (
    <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <div>
        <p>
          <strong>Krok 1 (1 pkt).</strong> Oblicz moc zdarzenia{" "}
          <InlineMath math="B" />: dokładnie {COUNT_TEXT[task.conditionCount]}{" "}
          {FACE_TEXT[task.conditionFace]}.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math="|B| =" />
          <EquationInput
            value={userAnswer.condition || ""}
            onChange={(v) => setField(userAnswer, "condition", v, onChange)}
            disabled={disabled}
            placeholder={`np. C(${task.n},${task.conditionCount})*5^${task.otherSlots}`}
            showPreview={true}
            basis="300px"
          />
        </div>
      </div>

      <div>
        <p>
          <strong>Krok 2 (1 pkt).</strong> Oblicz moc zdarzenia{" "}
          <InlineMath math={"A\\cap B"} />: zachodzi warunek z kroku 1 i pojawia
          się co najmniej raz {FACE_TEXT[task.targetFace]}.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math={"|A\\cap B| ="} />
          <EquationInput
            value={userAnswer.intersection || ""}
            onChange={(v) => setField(userAnswer, "intersection", v, onChange)}
            disabled={disabled}
            placeholder={`np. C(${task.n},${task.conditionCount})*(5^${task.otherSlots}-4^${task.otherSlots})`}
            showPreview={true}
            basis="360px"
          />
        </div>
      </div>

      <div>
        <p>
          <strong>Krok 3 (1 pkt).</strong> Oblicz prawdopodobieństwo warunkowe:
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math="P(A|B) =" />
          <EquationInput
            value={userAnswer.result || ""}
            onChange={(v) => setField(userAnswer, "result", v, onChange)}
            disabled={disabled}
            placeholder="np. 9/25"
            showPreview={true}
            basis="220px"
          />
        </div>
      </div>
    </div>
  );
}

export function checkAnswer(task, userAnswer = {}) {
  return scoreParts([
    {
      key: "condition",
      label: "moc zdarzenia B",
      correct: isValueCorrect(userAnswer.condition, task.conditionTotal),
    },
    {
      key: "intersection",
      label: "moc zdarzenia A∩B",
      correct: isValueCorrect(userAnswer.intersection, task.intersectionTotal),
    },
    {
      key: "result",
      label: "prawdopodobieństwo warunkowe",
      correct: isValueCorrect(userAnswer.result, task.probability, 1e-9),
    },
  ]);
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div className="space-y-1">
      <p>
        <InlineMath math={`|B|=${toLatexExpression(userAnswer.condition)}`} />
      </p>
      <p>
        <InlineMath math={`|A\\cap B|=${toLatexExpression(userAnswer.intersection)}`} />
      </p>
      <p>
        <InlineMath math={`P(A|B)=${toLatexExpression(userAnswer.result)}`} />
      </p>
    </div>
  );
}

export function renderAnswer(task) {
  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong>{" "}
        <InlineMath
          math={`|B|=\\binom{${task.n}}{${task.conditionCount}}\\cdot 5^{${task.otherSlots}}=${task.conditionTotal}`}
        />
      </p>
      <p>
        <strong>Krok 2:</strong>{" "}
        <InlineMath
          math={`|A\\cap B|=\\binom{${task.n}}{${task.conditionCount}}\\cdot \\left(5^{${task.otherSlots}}-4^{${task.otherSlots}}\\right)=${task.intersectionTotal}`}
        />
      </p>
      <p>
        <strong>Krok 3:</strong>{" "}
        <InlineMath
          math={`P(A|B)=\\frac{|A\\cap B|}{|B|}=${fractionLatex(
            task.intersectionTotal,
            task.conditionTotal
          )}`}
        />
      </p>
    </div>
  );
}

export function renderExplanation(task) {
  return (
    <div className="space-y-4">
      <p>
        <strong>Krok 1.</strong> Oznaczmy przez <InlineMath math="B" /> zdarzenie,
        że w <InlineMath math={String(task.n)}/> rzutach otrzymamy dokładnie {COUNT_TEXT[task.conditionCount]}{" "}
        {FACE_TEXT[task.conditionFace]}. Wybieramy pozycje tych wyników, a na
        pozostałych miejscach może pojawić się dowolny z pięciu innych wyników.
      </p>
      <BlockMath
        math={`|B|=\\binom{${task.n}}{${task.conditionCount}}\\cdot 5^{${task.otherSlots}}=${task.conditionTotal}`}
      />

      <p>
        <strong>Krok 2.</strong> Dla <InlineMath math={"A\\cap B"} /> dodatkowo
        wymagamy, żeby w pozostałych <InlineMath math={String(task.otherSlots)}/> rzutach co najmniej raz
        pojawiło się {FACE_TEXT[task.targetFace]}. Liczymy więc wszystkie
        możliwości bez wyniku <InlineMath math={String(task.conditionFace)}/> i odejmujemy te, w których
        nie ma także wyniku <InlineMath math={String(task.targetFace)}/>.
      </p>
      <BlockMath
        math={`|A\\cap B|=\\binom{${task.n}}{${task.conditionCount}}\\cdot \\left(5^{${task.otherSlots}}-4^{${task.otherSlots}}\\right)=${task.intersectionTotal}`}
      />

      <p>
        <strong>Krok 3.</strong> Korzystamy ze wzoru na prawdopodobieństwo
        warunkowe:
      </p>
      <BlockMath
        math={`P(A|B)=\\frac{|A\\cap B|}{|B|}=\\frac{${task.intersectionTotal}}{${task.conditionTotal}}=${fractionLatex(
          task.intersectionTotal,
          task.conditionTotal
        )}`}
      />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
